import "./styles.scss";

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomModal from "@components/CustomModal";
import { Input, Text, Icon } from "@fragments";
import { IconMailForward, IconUserPlus } from "@tabler/icons-react";

import api from "@helpers/api";

const INITIAL = { email: "", split_percentage: "", notes: "" };

/**
 * Send invitation — §6.5. Imperative open like ProgramEditorModal:
 *   modalRef.current.open(program, onSent)
 * Fields: e-mail (required) · optional split override · optional message. 422s
 * map to field errors + a toast (inactive program / duplicate enrollment).
 */
export default function InviteModal({ modalRef, communityId }) {
  const { t } = useTranslation("mentoringPrograms");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});

  const programRef = useRef(null);
  const onSentRef = useRef(null);

  modalRef.current = {
    open(program, onSent) {
      programRef.current = program ?? null;
      onSentRef.current = onSent;
      setForm(INITIAL);
      setErrors({});
      setOpen(true);
    },
  };

  const program = programRef.current;
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  async function handleSubmit() {
    setLoading(true);
    setErrors({});

    const payload = {
      email: form.email,
      split_percentage:
        form.split_percentage === "" ? null : Number(form.split_percentage),
      notes: form.notes || null,
    };

    const res = await api.post(
      `community/${communityId}/mentoring/programs/${program?.id}/invitations`,
      payload,
    );

    setLoading(false);

    if (res?.response?.status >= 400) {
      setErrors(mapErrors(res.response.data?.errors));
      window.setSnackbar?.(res.response.data?.message || t("invite.error"));
      return;
    }

    window.setSnackbar?.(t("invite.sent"));
    onSentRef.current?.(res.data);
    setOpen(false);
  }

  const splitHelper =
    errors.split_percentage ||
    (program?.split_percentage != null
      ? t("invite.splitHelperWithDefault", { percentage: program.split_percentage })
      : t("invite.splitHelper"));

  return (
    <CustomModal
      maxWidth={520}
      open={open}
      handleClose={() => setOpen(false)}
      title={t("invite.title")}
      bottomButtonSumbitText={t("invite.submit")}
      bottomButtonSumbitIcon={<IconMailForward size={18} />}
      handleSubmit={handleSubmit}
      loading={loading}
      bodyComponent={
        open ? (
          <div className="invite-modal">
            <div className="invite-modal-head">
              <Icon.Sphere size={56}>
                <IconUserPlus />
              </Icon.Sphere>
              <div className="invite-modal-head-text">
                <Text.h5 contrast={9} weight={500}>
                  {t("invite.headline")}
                </Text.h5>
                {program && (
                  <Text.span contrast={5} maxLines={1}>
                    {program.name}
                  </Text.span>
                )}
              </div>
            </div>

            <Input.Email
              label={t("invite.emailLabel")}
              value={form.email}
              onInput={(v) => set("email", v)}
              error={!!errors.email}
              helper={errors.email}
              autoFocus
            />
            <Input.Integer
              label={t("invite.splitLabel")}
              value={form.split_percentage}
              onInput={(v) => set("split_percentage", v)}
              max={3}
              end="%"
              error={!!errors.split_percentage}
              helper={splitHelper}
              placeholder={t("invite.splitPlaceholder")}
            />
            <Input.Text
              label={t("invite.notesLabel")}
              value={form.notes}
              onInput={(v) => set("notes", v)}
              multiline
              placeholder={t("invite.notesPlaceholder")}
            />
          </div>
        ) : null
      }
    />
  );
}

function mapErrors(fieldErrors = {}) {
  return Object.entries(fieldErrors).reduce((acc, [key, messages]) => {
    acc[key] = Array.isArray(messages) ? messages[0] : String(messages);
    return acc;
  }, {});
}
